import React, {useEffect, useRef, useState} from "react";

const readToken = () => {
    const cookieToken = (document.cookie.match(/(?:^|;\s*)user_token=([^;]+)/) || [])[1];
    if (cookieToken) return decodeURIComponent(cookieToken);
    try { return localStorage.getItem('jfd_token') || null; } catch (e) { return null; }
};

// Per-game presentation is fully driven by props — the component holds no
// title-based branching. Callers can use one of the preset wrappers below
// (ChaosCharactersScores, TrilemmaScores, FinalScores) or pass props
// directly for ad-hoc usage.
export const HighScores = ({
    isControl,
    title,
    scoresEndpoint,
    logoSrc,
    playerSubtitle = 'Top Scores',
    playerBackdropVideoSrc,
    playerBackdropPosterSrc,
    playerOverlayBackground,
}) => {
    const [highScores, setHighScores] = useState();
    const [currentToken, setCurrentToken] = useState(() => isControl ? null : readToken());


    useEffect(() => {
        fetch(scoresEndpoint)
            .then(response => response.json())
            .then(setHighScores)
    }, []);

    useEffect(() => {
        if (isControl) return;
        fetch('/users/current', {credentials: 'include'})
            .then(r => r.ok ? r.json() : null)
            .then(u => u && u.token && setCurrentToken(u.token))
            .catch(() => {});
    }, [isControl]);

    if (isControl) return <HighScoresControl title={title} highScores={highScores} logoSrc={logoSrc} />;
    return <HighScoresPlayer
        title={title}
        highScores={highScores}
        currentToken={currentToken}
        logoSrc={logoSrc}
        subtitle={playerSubtitle}
        backdropVideoSrc={playerBackdropVideoSrc}
        backdropPosterSrc={playerBackdropPosterSrc}
        overlayBackground={playerOverlayBackground}
    />;
};

// Convenience wrappers so call sites stay tight while the component itself
// stays config-free.
export const TrilemmaScores = (props) => <HighScores
    title="Trilemma"
    scoresEndpoint="/trilemma/get-scores"
    logoSrc="/trilemma-assets/logo.webp"
    playerBackdropVideoSrc="/trilemma-assets/backdrop.mp4"
    playerBackdropPosterSrc="/trilemma-assets/backdrop.webp"
    playerOverlayBackground="linear-gradient(#0a1530, rgba(10,20,50,0.25))"
    {...props}
/>;

export const DancelestrationsHighScores = (props) => <HighScores
    title="Dancelestrations"
    scoresEndpoint="/dancelestrations/get-high-scores"
    logoSrc="/dancelestrations-assets/logo-white.png"
    playerBackdropPosterSrc="/dancelestrations-assets/backdrop.webp"
    playerOverlayBackground="linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6))"
    {...props}
/>;

export const ChaosCharactersScores = (props) => <HighScores
    title="Chaos Characters"
    scoresEndpoint="/chaos-characters/get-high-scores"
    logoSrc="/chaos-characters-assets/logo.webp"
    playerBackdropVideoSrc="/chaos-characters-assets/backdrop.mp4"
    playerBackdropPosterSrc="/chaos-characters-assets/backdrop.webp"
    playerOverlayBackground="linear-gradient(rgba(230,41,158,1), rgba(230,41,158,0))"
    {...props}
/>;

export const FinalScores = (props) => <HighScores
    title="Final Scores"
    scoresEndpoint="/leaderboard/get-leaderboard?type=end"
    logoSrc="/results-logo.png"
    playerSubtitle="Final Scores"
    playerBackdropPosterSrc="/results-assets/backdrop.jpeg"
    playerOverlayBackground="linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6))"
    {...props}
/>;

export const STROKE_FILTER = 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)';

// Count-up/down number for verdicts and reveals. Green while gaining, red
// while draining, settles gold.
export const AnimatedPoints = ({from, to, go, duration = 2500}) => {
    const [value, setValue] = useState(from);
    useEffect(() => {
        if (!go) { setValue(from); return; }
        let raf = null;
        const startAt = performance.now();
        const tick = (now) => {
            const progress = Math.min((now - startAt) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(from + (to - from) * eased));
            if (progress < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => { if (raf) cancelAnimationFrame(raf); };
    }, [go, from, to, duration]);

    const settled = go && value === to;
    const gaining = to > from;
    return <div style={{
        fontSize: 110,
        fontWeight: 900,
        lineHeight: 1,
        color: !go || settled ? '#f1d28b' : (gaining ? '#7dff96' : '#ff5a3a'),
        textShadow: '0 0 24px rgba(232,177,90,0.4), 3px 3px 0 rgba(0,0,0,0.9)',
        transition: 'color 400ms',
    }}>{value}</div>;
};

export const rankAccent = (rank) => {
    if (rank === 1) return '#f1d28b'; // gold
    if (rank === 2) return '#d8d8d8'; // silver
    if (rank === 3) return '#cd7f32'; // bronze
    return '#ffffff';
};

const HighScoresPlayer = ({
    title,
    highScores,
    currentToken,
    logoSrc,
    subtitle,
    backdropVideoSrc,
    backdropPosterSrc,
    overlayBackground,
}) => {
    if (!highScores) return null;

    // Bubble the current player to the top of their rank group so ties show
    // the player first (and they appear in the visible top 5 if their rank
    // alone qualifies).
    const sortedScores = currentToken
        ? [...highScores].sort((a, b) => {
            if (a.rank !== b.rank) return a.rank - b.rank;
            if (a.character.token === currentToken) return -1;
            if (b.character.token === currentToken) return 1;
            return 0;
        })
        : highScores;

    const playerEntry = currentToken
        ? sortedScores.find(s => s.character.token === currentToken)
        : null;
    const top5 = sortedScores.slice(0, 5);
    const playerInTop5 = playerEntry && top5.some(s => s.character.token === currentToken);
    const top = sortedScores.slice(0, playerEntry && !playerInTop5 ? 4 : 5);
    const visibleScores = playerEntry && !playerInTop5 ? [...top, playerEntry] : top;

    return <div style={{
        position: 'relative',
        width: '100%',
        minHeight: '100dvh',
        alignSelf: 'flex-start',
        boxSizing: 'border-box',
        padding: 'calc(20px + env(safe-area-inset-top)) 0 calc(24px + env(safe-area-inset-bottom))',
        color: 'white',
    }}>
        {backdropVideoSrc ? (
            <video
                src={backdropVideoSrc}
                poster={backdropPosterSrc}
                autoPlay loop muted playsInline preload="auto"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: -2,
                    pointerEvents: 'none',
                }}
            />
        ) : backdropPosterSrc ? (
            <img
                src={backdropPosterSrc}
                alt=""
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: -2,
                    pointerEvents: 'none',
                }}
            />
        ) : null}
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundImage: overlayBackground,
            backgroundColor: overlayBackground ? undefined : 'rgba(0,0,0,0.45)',
            zIndex: -1,
            pointerEvents: 'none',
        }}/>

        {logoSrc ? (
            <img
                src={logoSrc}
                alt={title}
                style={{
                    display: 'block',
                    width: '85%',
                    maxWidth: 380,
                    margin: '0 auto 4px',
                    filter: 'drop-shadow(3px 3px 0 rgba(0,0,0,0.8))',
                }}
            />
        ) : (
            <div style={{
                textAlign: 'center',
                fontSize: 32,
                fontWeight: 900,
                letterSpacing: 4,
                textTransform: 'uppercase',
                color: '#f1d28b',
                textShadow: '0 0 18px rgba(232,177,90,0.7), 2px 2px 0 rgba(0,0,0,0.95)',
                marginBottom: 8,
                fontStyle: 'italic',
            }}>{title}</div>
        )}
        <div style={{
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 5,
            textTransform: 'uppercase',
            marginBottom: 22,
        }}>{subtitle}</div>

        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            {visibleScores.map((score, i) => {
                const accent = '#ffffff';
                const isYou = currentToken && score.character.token === currentToken;
                const isFloater = playerEntry && !playerInTop5 && i === visibleScores.length - 1;
                const youHighlight = isYou;
                return <div key={score.character.token} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    paddingLeft: 14,
                    paddingRight: 14,
                    height: 84,
                    overflow: 'hidden',
                    position: 'relative',
                    borderTop: youHighlight ? '2px solid #f1d28b' : '1px solid rgba(255,255,255,0.12)',
                    borderBottom: youHighlight ? '2px solid #f1d28b' : '1px solid rgba(255,255,255,0.12)',
                    backgroundColor: youHighlight ? 'rgba(232,177,90,0.85)' : 'rgba(0,0,0,0.92)',
                    boxShadow: youHighlight ? 'inset 0 0 24px rgba(232,177,90,0.25)' : 'inset 0 0 18px rgba(0,0,0,0.5)',
                    marginTop: isFloater ? 18 : 0,
                }}>
                    <div style={{
                        fontSize: 32,
                        fontWeight: 900,
                        fontStyle: 'italic',
                        color: accent,
                        width: 28,
                        textAlign: 'center',
                        flexShrink: 0,
                        textShadow: `0 0 12px ${accent}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
                    }}>{score.rank}</div>
                    <img
                        src={score.character.image_url}
                        alt={score.character.name}
                        style={{
                            width: 110,
                            height: 110,
                            objectFit: 'cover',
                            objectPosition: 'top',
                            alignSelf: 'flex-start',
                            flexShrink: 0,
                            willChange: 'filter',
                            filter: STROKE_FILTER,
                        }}
                    />
                    <div style={{flex: 1, minWidth: 0}}>
                        <div style={{
                            fontSize: 22,
                            fontWeight: 800,
                            lineHeight: 1.1,
                            textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>{score.character.name}</div>
                        <div style={{
                            fontSize: 15,
                            fontStyle: 'italic',
                            opacity: 0.85,
                            lineHeight: 1.15,
                            textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                            wordBreak: 'break-word',
                        }}>{score.character.nickname}</div>
                    </div>
                    <div style={{
                        fontSize: 36,
                        fontWeight: 900,
                        color: accent,
                        textShadow: `0 0 12px ${accent}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
                    }}>{score.score}</div>
                </div>;
            })}
        </div>
    </div>
}

export const HighScoresControl = ({title, highScores, logoSrc}) => {
    const renderScores = (scores) =>
        scores.map((score) =>
            <div key={score.character.token} className={`highscores-${score.rank}`} style={{
                margin: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                boxSizing: 'border-box',
                height: '146px',
                overflow: 'hidden',
                position: 'relative',
            }}>
                <div style={{textAlign: 'center', paddingRight: 6, paddingLeft: 6, width: 50, zIndex: 1, flexShrink: 0}}>
                    <div style={{
                        fontSize: 64,
                        fontWeight: 900,
                        fontStyle: 'italic',
                        color: rankAccent(score.rank),
                        textShadow: `0 0 18px ${rankAccent(score.rank)}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
                    }}>{score.rank}</div>
                </div>
                <img
                    src={score.character.image_url}
                    alt={score.character.name}
                    style={{
                        width: 220,
                        height: 220,
                        objectFit: 'cover',
                        objectPosition: 'top',
                        alignSelf: 'flex-start',
                        flexShrink: 0,
                    }}
                />
                <div style={{paddingLeft: 10, flex: 1, textAlign: 'left', minWidth: 0}}>
                    <div style={{
                        fontSize: 40,
                        fontWeight: 800,
                        lineHeight: 1.05,
                        textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>{score.character.name}</div>
                    <div style={{
                        fontSize: 32,
                        fontStyle: 'italic',
                        lineHeight: 1.05,
                        opacity: 0.9,
                        textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                        wordBreak: 'break-word',
                    }}>{score.character.nickname}</div>
                </div>
                <div style={{fontSize: 76, marginLeft: 8, marginRight: 12, textAlign: 'right', flexShrink: 0}}>
                    {score.score}
                </div>
            </div>
        )

    if(!highScores) return null;

    return <div style={{textAlign: 'center', fontSize: 80, flexDirection: 'column', width: '1500px', height: '900px', display: 'flex', alignItems: 'center', alignSelf: 'flex-start', justifyContent: 'flex-start'}}>
        {logoSrc ? (
            <img
                src={logoSrc}
                alt={title}
                style={{
                    display: 'block',
                    height: 150,
                    marginTop: 0,
                    marginBottom: -60,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(4px 4px 0 rgba(0,0,0,0.8))',
                }}
            />
        ) : title ? (
            <div className={'highscores-title'} style={{margin: 2}}>{title}</div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', width: '100%'}}>
            <div style={{flex: 1, minWidth: 0, margin: 12}}>{renderScores(highScores.slice(0, 5))}</div>
            <div style={{flex: 1, minWidth: 0, margin: 12}}>{renderScores(highScores.slice(5, 10))}</div>
        </div>
    </div>
}

export const ScoreCounter = ({ score, highlightColor }) => {
    const [displayScore, setDisplayScore] = useState(score);
    const [isChanging, setIsChanging] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (displayScore !== score) {
            setIsChanging(true);
            timeoutRef.current = setTimeout(() => {
                setDisplayScore(prevScore => {
                    if (prevScore < score) {
                        return Math.min(prevScore + 1, score);
                    } else if (prevScore > score) {
                        return Math.max(prevScore - 1, score);
                    }
                    return score;
                });
            }, 50);
        } else {
            setIsChanging(false);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [displayScore, score]);

    const textStyle = {
        color: isChanging && highlightColor ? highlightColor : 'inherit',
        transition: 'color 0.3s ease'
    };

    return <span style={textStyle}>{displayScore}</span>;
};